import { UI } from 'common';
import React, { useEffect, useRef, useState } from 'react';

const { WrappedInputNumber, Typography } = UI;

const MAX_SEATS = 999;

interface SeatStepperProps {
	value: number;
	min: number;
	onChange: (value: number) => void;
	seatPrice?: number;
	autoFocus?: boolean;
	highlight?: boolean;
	dataqa?: string;
	disabled?: boolean;
	legacyMode?: boolean;
}

const SeatStepper = ({
	value,
	min,
	onChange,
	seatPrice = 5,
	autoFocus = false,
	highlight = false,
	dataqa = 'seat-stepper',
	disabled = false,
	legacyMode = false,
}: SeatStepperProps) => {
	const [isHighlighted, setIsHighlighted] = useState(false);
	const wrapperRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		if (highlight) {
			setIsHighlighted(true);
			const timer = setTimeout(() => setIsHighlighted(false), 3000);
			return () => clearTimeout(timer);
		}
	}, [highlight]);

	useEffect(() => {
		if (autoFocus && wrapperRef.current) {
			const input = wrapperRef.current.querySelector('input');
			if (input) {
				input.focus();
				input.select();
			}
		}
	}, [autoFocus]);

	const handleChange = (e: { target: { value: number } }) => {
		const newValue = e.target.value;
		if (newValue != null && newValue >= min && newValue <= MAX_SEATS) {
			onChange(newValue);
		}
	};

	const atMin = value <= min;
	const atMax = value >= MAX_SEATS;

	const wrapperClassName = [
		'seat-stepper',
		isHighlighted ? 'seat-stepper--highlight' : '',
		atMin ? 'seat-stepper--at-min' : '',
		atMax ? 'seat-stepper--at-max' : '',
	]
		.filter(Boolean)
		.join(' ');

	return (
		<div ref={wrapperRef} className={wrapperClassName}>
			<Typography.HeadingLabel className="seat-stepper__label">
				User Seats
			</Typography.HeadingLabel>
			<WrappedInputNumber
				value={value}
				min={min}
				max={MAX_SEATS}
				onChange={handleChange}
				showButtons={!disabled}
				dataqa={dataqa}
				className="seat-stepper__input"
				disabled={disabled}
			/>
			<Typography.P className="seat-stepper__helper">
				{legacyMode ? (
					`Your plan includes ${value} user ${value === 1 ? 'seat' : 'seats'}. Pick a new subscription plan to add user seats.`
				) : (
					<>
						Plan includes {min} user {min === 1 ? 'seat' : 'seats'}.
						<br />
						Pay ${seatPrice}/mo for each additional seat.
					</>
				)}
			</Typography.P>
		</div>
	);
};

export default SeatStepper;
